import type { Dispatch, SetStateAction } from 'react'
import { useSelector } from 'react-redux'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import {
  type Nil,
  type Question,
  type QuestionAnswer,
  QuestionAnswerTypes,
} from '@pbt/pbt-ui-components'

import type { AnswerInputType } from '~/api/graphql/generated/types'
import { getAnswerInputTypes } from '~/store/reducers/constants'

import MultipleSelectionOptions from './MultipleSelectionOptions'
import QuantitySelectorOptions from './QuantitySelectorOptions'
import { getAnswerTypeFlags } from './questionUtils'

const QuestionInput = ({
  buttonText,
  disableAnswer,
  handleDeleteAnswer,
  handleEditAnswer,
  inputType,
  onAddAnswerRequested,
  placeholder,
  question,
  setQuestionCandidate,
}: {
  buttonText: string
  disableAnswer: boolean
  handleDeleteAnswer?: (answer: QuestionAnswer) => void
  handleEditAnswer: (answer: QuestionAnswer, index?: number) => void
  inputType: string
  onAddAnswerRequested: () => void
  placeholder?: string
  question: Partial<Question> | Nil
  setQuestionCandidate: Dispatch<SetStateAction<Partial<Question> | Nil>>
}) => {
  const AnswerInputTypes: AnswerInputType[] = useSelector(getAnswerInputTypes)
  const {
    isCheckboxesType,
    isMultiChoiceType,
    isMultipleSelectionType,
    isQuantitySelectorType,
  } = getAnswerTypeFlags(inputType, AnswerInputTypes)

  const getDisplayOptionsForAnswerType = (answerType: string) =>
    AnswerInputTypes.find((type) => type.name === answerType)
      ?.answerInputSubTypes || []
  const answersLength = question?.answers?.length || 0

  const hasMultipleSelectionAnswers = Boolean(
    isMultipleSelectionType && question?.answers && answersLength > 0,
  )

  const handleAnswerOrderChange = (oldIndex: number, newIndex: number) => {
    const result = Array.from(question?.answers || [])
    const [removed] = result.splice(oldIndex, 1)
    result.splice(newIndex, 0, removed)

    setQuestionCandidate({
      ...(question || {}),
      answers: result,
    })
  }

  const onDisplayOptionChange = (option: string) => {
    setQuestionCandidate({
      ...(question || {}),
      answerInputSubtypeId: option,
    })
  }
  return (
    <>
      {isCheckboxesType && (
        <MultipleSelectionOptions
          buttonText={buttonText}
          disableAnswer={disableAnswer}
          displayOptions={getDisplayOptionsForAnswerType(
            QuestionAnswerTypes.CHECKBOXES,
          )}
          hasMultipleSelectionAnswers={hasMultipleSelectionAnswers}
          inputIcon={<CheckBoxOutlineBlankIcon color="disabled" />}
          isFollowUpQuestion={question?.followUp}
          placeholder={placeholder || ''}
          questionCandidate={question as Question}
          onAddAnswerRequested={onAddAnswerRequested}
          onDelete={handleDeleteAnswer}
          onDisplayOptionChange={onDisplayOptionChange}
          onEdit={handleEditAnswer}
          onOrderChange={handleAnswerOrderChange}
        />
      )}
      {isMultiChoiceType && (
        <MultipleSelectionOptions
          buttonText={buttonText}
          disableAnswer={disableAnswer}
          displayOptions={getDisplayOptionsForAnswerType(
            QuestionAnswerTypes.MULTIPLE_CHOICE,
          )}
          hasMultipleSelectionAnswers={hasMultipleSelectionAnswers}
          inputIcon={<RadioButtonUncheckedIcon color="disabled" />}
          isFollowUpQuestion={question?.followUp}
          placeholder={placeholder || ''}
          questionCandidate={question as Question}
          onAddAnswerRequested={onAddAnswerRequested}
          onDelete={handleDeleteAnswer}
          onDisplayOptionChange={onDisplayOptionChange}
          onEdit={handleEditAnswer}
          onOrderChange={handleAnswerOrderChange}
        />
      )}
      {isQuantitySelectorType && (
        <QuantitySelectorOptions
          disableAnswer={disableAnswer}
          questionCandidate={question as Question}
          setQuestionCandidate={setQuestionCandidate}
        />
      )}
    </>
  )
}

export default QuestionInput
